import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  IColorByValueAttributeMap,
  IColorByValueConfig,
  useUpsertColorByValueConfigMutation
} from 'app/services/api/colorByValue'
import CustomizeModalActionBar from 'common/components/Modal/CustomizeModalActionBar'
import DetailsModal from 'common/components/Modal/DetailsModal'

import Content from './Content'
import Sidebar from './Sidebar'

interface IColorByValueCustomizeColorModal {
  config: IColorByValueConfig
  isModalOpen: boolean
  setIsModalOpen: (isModalOpen: boolean) => void
  isModalLoading: boolean
  setIsModalLoading: (isModalLoading: boolean) => void
}

const ColorByValueCustomizeColorModal = ({
  config,
  isModalOpen,
  setIsModalOpen,
  setIsModalLoading
}: IColorByValueCustomizeColorModal) => {
  const [persistCBVConfig] = useUpsertColorByValueConfigMutation()

  const [stagingConfig, setStagingConfig] =
    useState<IColorByValueConfig>(config)

  const resetStagingConfig = () => {
    const selectedStagingAttribute = config.attributesMap.find(
      (attr) => attr.attributeId === stagingConfig.selectedAttributeId
    )!

    const attributeCopy = {
      ...selectedStagingAttribute
    }

    updateCurrentSelectedStagingAttributeMap(attributeCopy)
  }

  useEffect(() => {
    if (isModalOpen && config != null) {
      const tempConfig = { ...config }

      if (
        tempConfig.selectedAttributeId == null &&
        config.attributesMap.length > 0
      ) {
        tempConfig.selectedAttributeId = config.attributesMap[0].attributeId
      }

      setStagingConfig(tempConfig)
    }
  }, [config, isModalOpen])

  const currentSelectedStagingAttributeMap =
    useMemo((): IColorByValueAttributeMap | null => {
      if (stagingConfig.selectedAttributeId == null) {
        return null
      }

      const selectedStagingAttributeMap = stagingConfig.attributesMap.find(
        (attr) => attr.attributeId === stagingConfig.selectedAttributeId
      )

      return selectedStagingAttributeMap ?? null
    }, [stagingConfig.attributesMap, stagingConfig.selectedAttributeId])

  const updateCurrentSelectedStagingAttributeMap = (
    attributeMap: IColorByValueAttributeMap
  ): void => {
    if (stagingConfig.selectedAttributeId == null) {
      // NOTE: Impossible scenario, type guard
      return
    }

    const selectedStagingAttributeMapIndex =
      stagingConfig.attributesMap.findIndex(
        (attr) => attr.attributeId === stagingConfig.selectedAttributeId
      )

    const newAttributesMap = [...stagingConfig.attributesMap]

    newAttributesMap.splice(selectedStagingAttributeMapIndex, 1, attributeMap)

    setStagingConfig({
      ...stagingConfig,
      attributesMap: newAttributesMap
    })
  }

  const applyConfig = () => {
    const toastRef = toast.info('Saving Color by Value configuration')

    setIsModalLoading(true)

    persistCBVConfig(stagingConfig)
      .then(() => {
        toast.dismiss(toastRef)
        toast.success('Color by Value configuration save success')
        setIsModalLoading(false)
        closeModal()
      })
      .catch(() => {
        toast.dismiss(toastRef)
        toast.error('Color by Value update failed')
        setIsModalLoading(false)
      })
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const [dataErrors, setDataErrors] = useState<{ [key: string]: boolean }>({})

  const canApply = () => {
    const hasErrors = Object.values(dataErrors).some(Boolean)

    return !hasErrors
  }

  return (
    <DetailsModal
      title='Customize Colors'
      open={isModalOpen}
      close={closeModal}
      className='h-3/4 min-w-[52rem]'
      headerClassName='flex px-6 py-4'
      footer={
        <CustomizeModalActionBar
          apply={applyConfig}
          cancel={closeModal}
          canApply={canApply}
        />
      }
    >
      <div
        className={[
          'flex min-h-50vh min-w-50vw flex-auto',
          'border-b border-t border-gray-300'
        ].join(' ')}
      >
        <Sidebar config={stagingConfig} setConfig={setStagingConfig} />
        <div className='border-l border-l-gray-300' />
        <Content
          attributeMap={currentSelectedStagingAttributeMap}
          setAttributeMap={updateCurrentSelectedStagingAttributeMap}
          resetStagingConfig={resetStagingConfig}
          setDataErrors={setDataErrors}
        />
      </div>
    </DetailsModal>
  )
}

export default ColorByValueCustomizeColorModal
